import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, Clock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [participantEmail, setParticipantEmail] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadEventDetails();
  }, [id]);

  const loadEventDetails = async () => {
    try {
      const [eventRes, attendanceRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/attendance/event/${id}`)
      ]);
      
      const foundEvent = eventRes.data.find(e => e.id === id);
      setEvent(foundEvent);
      setAttendance(attendanceRes.data);
    } catch (error) {
      console.error("Failed to load event:", error);
      toast.error("Failed to load event details");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAttendance = async () => {
    if (!participantEmail) {
      toast.error("Please enter your email");
      return;
    }

    setConfirming(true);
    try {
      await axios.post(`${API}/attendance/confirm-by-email`, {
        email: participantEmail,
        event_id: id
      });

      toast.success("Attendance confirmed!");
      setDialogOpen(false);
      setParticipantEmail("");
      loadEventDetails();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Email not registered. Please register first.");
      } else if (error.response?.status === 403) {
        toast.error("Your registration is pending approval or your account is suspended.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to confirm attendance");
      }
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-crew-black flex items-center justify-center">
        <div className="text-zinc-500 text-xl">Loading event details...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-crew-black flex items-center justify-center">
        <div className="text-zinc-500 text-xl">Event not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-crew-black relative grain-overlay">
      <Navigation userType="public" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 max-w-4xl">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-6 text-zinc-400 hover:text-crew-yellow"
          data-testid="back-button"
        >
          <Calendar className="mr-2 h-4 w-4" /> Return to Previous Screen
        </Button>

        <div className="bg-crew-zinc border border-zinc-800 p-8 rounded-sm relative overflow-hidden mb-6">
          <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-crew-yellow"></div>
          
          <h1 className="text-4xl font-black text-crew-yellow mb-6" data-testid="event-name">
            {event.name}
          </h1>

          {(event.livestream_youtube || event.recording_required) && (
            <div className="flex flex-wrap gap-2 -mt-3 mb-6" data-testid="event-av-badges">
              {event.livestream_youtube && (
                <span className="text-xs font-bold px-2 py-1 rounded bg-red-500/20 text-red-300" data-testid="event-livestream-badge">● LIVESTREAM (YouTube)</span>
              )}
              {event.recording_required && (
                <span className="text-xs font-bold px-2 py-1 rounded bg-blue-500/20 text-blue-300" data-testid="event-recording-badge">● RECORDING</span>
              )}
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="flex items-center gap-3">
              <Calendar className="text-crew-yellow" size={24} />
              <div>
                <div className="text-zinc-500 text-xs uppercase font-bold">Date</div>
                <div className="text-white font-mono" data-testid="event-date">{event.date}</div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Clock className="text-crew-yellow" size={24} />
              <div>
                <div className="text-zinc-500 text-xs uppercase font-bold">Call Time</div>
                <div className="text-white font-mono" data-testid="event-time">{event.call_time}</div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Users className="text-crew-yellow" size={24} />
              <div>
                <div className="text-zinc-500 text-xs uppercase font-bold">Confirmed</div>
                <div className="text-white font-mono" data-testid="attendee-count">{attendance.length}</div>
              </div>
            </div>
          </div>

          {event.description && (
            <div className="mb-8">
              <h3 className="text-zinc-400 text-sm uppercase font-bold mb-2">Description</h3>
              <p className="text-zinc-300" data-testid="event-description">{event.description}</p>
            </div>
          )}

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-crew-yellow text-black font-bold uppercase tracking-widest hover:bg-white transition-colors rounded-sm px-8 py-6 text-lg shadow-brutal"
                data-testid="confirm-attendance-button"
              >
                Confirm My Attendance
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-crew-zinc border-2 border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle className="text-crew-yellow text-2xl font-bold">CONFIRM ATTENDANCE</DialogTitle>
                <DialogDescription className="text-zinc-400">
                  Enter your details to confirm attendance for this event.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="participant-email" className="text-zinc-300 font-bold uppercase text-xs">Your Registered Email</Label>
                  <Input
                    id="participant-email"
                    type="email"
                    value={participantEmail}
                    onChange={(e) => setParticipantEmail(e.target.value)}
                    placeholder="email@example.com"
                    className="bg-black border-2 border-zinc-800 focus:border-crew-yellow text-white"
                    data-testid="participant-email-input"
                  />
                  <p className="text-xs text-zinc-500 mt-2">
                    Not a crew member yet? <span className="text-crew-yellow cursor-pointer hover:underline" onClick={() => navigate('/register')}>Apply here</span>
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={handleConfirmAttendance}
                  disabled={confirming}
                  className="bg-crew-yellow text-black font-bold hover:bg-white"
                  data-testid="submit-attendance-button"
                >
                  {confirming ? "Confirming..." : "Confirm Attendance"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Attendee List */}
        <div className="bg-crew-zinc border border-zinc-800 rounded-sm overflow-hidden">
          <div className="p-6 border-b border-zinc-800">
            <h2 className="text-2xl font-bold text-crew-yellow" data-testid="attendees-title">
              CONFIRMED CREW ({attendance.length})
            </h2>
          </div>
          
          {attendance.length === 0 ? (
            <div className="p-8 text-center text-zinc-500" data-testid="no-attendees">
              No confirmed attendees yet. Be the first!
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {attendance.map((record, idx) => (
                <div key={record.participant?.id || idx} className="p-4 hover:bg-zinc-900 transition-colors" data-testid={`attendee-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-white">
                        {record.participant.first_name} {record.participant.last_name}
                      </div>
                      <div className="text-sm text-zinc-500">
                        {record.participant.preferred_name && `"${record.participant.preferred_name}"`}
                      </div>
                    </div>
                    <div className="text-xs text-zinc-500 font-mono">
                      Confirmed: {new Date(record.confirmed_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetails;
